import { Link, useParams, Navigate } from "react-router-dom";
import Reveal from "@/components/Reveal";
import WordReveal from "@/components/WordReveal";
import {
  CASE_STUDY_CATEGORIES,
  getCaseStudyCategory,
  getCaseStudiesByCategory,
  caseStudies,
} from "@/data/caseStudies";
import { useSeo, SITE_URL } from "@/lib/seo";
import { breadcrumb } from "@/lib/structuredData";

// Per-category context: overview intro, related services, related guides, FAQs.
// Each category hub delivers a topical-cluster page that Google and buyers can
// consume as a self-contained authority page.
const CATEGORY_CONTEXT = {
  lighting: {
    overview: [
      "Decorative, architectural and hospitality lighting sourced across Moradabad brass anchors, Firozabad hand-blown glass studios and Delhi NCR electrical assembly. Every SKU documented against UL 153 / IEC 60598-1 / G-Mark / SASO as required by destination market.",
      "Our lighting programmes cover the full spectrum: retail-catalogue table lamps and pendants for US specialty retailers, hospitality FF&E for 5-star GCC properties, architectural pendant fit-outs for European contract specifiers, and multi-decade retail programmes for luxury UK brands.",
    ],
    faqs: [
      { q: "Can Indian factories deliver UL 153 certified table lamps for US retail?", a: "Yes — with the right partner enforcing UL Follow-Up Service discipline at the electrical-assembly factory. Our programmes hold 100% first-submission UL 153 pass rate across six years." },
      { q: "What is LM-79 photometric certification and why does it matter for contract lighting?", a: "LM-79 photometric testing produces the light-output, colour-temperature, CRI and distribution data that MEP consultants need to design a building's lighting-load. Contract-lighting specifiers require it per SKU." },
      { q: "How is hand-blown glass transit-breakage held below 0.5%?", a: "Individual-cell EVA-foam separators or corrugated-cell inserts per ornament. Annealing schedule enforced upstream. Loading-day supervision on every container." },
      { q: "Can Moradabad supply hospitality FF&E at 5-star specification?", a: "Yes — Moradabad delivers craft-tier brass fixtures at hospitality-specification quality when the sourcing programme runs on 5-star project discipline (SASO-experienced anchor factory, phased container delivery, site-installation supervision)." },
    ],
  },
  furniture: {
    overview: [
      "Solid-wood and mixed-material furniture programmes sourced from Jodhpur, Saharanpur and Delhi NCR. FSC-certified sheesham and mango wood, kiln-dried to 8-12% moisture content, joinery-library engineered for humidity-delta post-shipment stability, and retail flat-pack packaging engineered from CAD stage.",
      "Programmes cover Scandinavian modernism, French provincial, mid-century American, and Rajasthani craft-tier — with the design-intent-translation layer that separates viable Indian furniture sourcing from broker-model programmes.",
    ],
    faqs: [
      { q: "Can Jodhpur match Vietnam on solid-wood furniture cost?", a: "Jodhpur delivers craft-tier hand-work and joinery-library depth that Vietnam does not natively possess, at competitive cost provided the upstream material-science discipline (moisture control, kiln discipline, joinery specification) is enforced." },
      { q: "How is zero-rejection at DC receipt achieved?", a: "Kiln-drying to 8-12% moisture content verified per PO, joinery library engineered for humidity delta, retail flat-pack hardware kit inside every box. Our Scandinavian programme holds zero-rejection across 22 consecutive containers." },
      { q: "How is FSC chain-of-custody enforced across the wood supply chain?", a: "Log source traceability from FSC-certified forest through log yard, sawmill, kiln, and factory. Transaction certificate per shipment." },
      { q: "Can we hold a rolling quarterly launch cadence on solid-wood furniture?", a: "Yes — with a documented product-development calendar, per-quarter deliverables, and locked factory capacity. Our Scandinavian programme runs 4-8 SKUs per quarter across six years." },
    ],
  },
  "home-decor": {
    overview: [
      "Home décor programmes sourced across all fifteen major Indian clusters — brass Moradabad, ceramic Jaipur, textile Panipat, wood Saharanpur, glass Firozabad, marble Rajasthan, hand-knotted rugs Bhadohi, Kashmir paper-mache, and Delhi NCR consolidation. Mixed-material assembly at Delhi NCR consolidation partner enables retail programmes at multi-cluster scale.",
      "Our home décor case studies document 200-SKU multi-material collections, 620-SKU brass expansion programmes, 340-SKU mirror ranges, and Christmas-plus-Diwali seasonal drops across global retail geographies.",
    ],
    faqs: [
      { q: "Can India deliver multi-material home décor at retail-catalogue scale?", a: "Yes — through Delhi NCR hub consolidation across six or more clusters, with GRS + FSC + GoodWeave chain-of-custody documentation ingested directly into the retailer's sustainability reporting portal." },
      { q: "How do you manage six cluster calendars into a single monthly container?", a: "Rolling 12-month capacity plan locks volumes at cluster level in advance of the operating quarter. Brass Moradabad annual volume locks in November, ceramic Jaipur is weather-forecast dependent, textile Panipat is yarn-contract dependent." },
      { q: "How is retail-shipping breakage held below 0.5% on fragile home décor?", a: "Individual-cell EVA-foam or corrugated-cell separators per fragile SKU. Retail box drop-tested to ISTA-3A for DTC parcel. Loading-day supervision on every container." },
    ],
  },
  glass: {
    overview: [
      "Hand-blown and moulded decorative glass programmes from Firozabad — the world's largest concentration of hand-blown glass craft. Furnace-slot allocation locked 12 months forward. Colour tolerance band documented per SKU. Wall-thickness and annealing schedule engineered against transit-vibration profile.",
      "Case studies cover Christmas ornament and Diwali seasonal drops, hand-blown pendant shades for hospitality lighting, and hand-crafted glass vessels for European retail.",
    ],
    faqs: [
      { q: "Why source hand-blown decorative glass from Firozabad?", a: "Firozabad is the world's largest concentration of hand-blown decorative-glass craft, with a scale and colour-family depth that European hand-blown studios cannot match at commercial cost." },
      { q: "How is colour consistency managed on hand-blown glass?", a: "Every SKU has a documented colour tolerance band (three physical reference tiles: mid-point, upper bound, lower bound). Batch QC rejects out-of-band production before container loading." },
      { q: "How is transit-breakage held below 0.5% on fragile ornaments?", a: "Individual-cell EVA-foam separators per ornament. Master carton compression rated for four-carton stack. Annealing schedule enforced upstream." },
    ],
  },
  ceramics: {
    overview: [
      "Ceramic sourcing from Jaipur — blue pottery (GI-tagged Rajasthan craft), stoneware, terracotta and food-contact ceramics. Body-composition and glaze-chemistry documented per SKU family. Freeze-thaw safety verified for cold-climate destinations. Food-contact glaze specification lead-free, cadmium-free.",
      "Case studies cover Blue Pottery packaging redesign (17% to <0.8% breakage), premium ceramic table-lamp bases, and food-contact kitchen ceramics for European retail.",
    ],
    faqs: [
      { q: "Can Jaipur blue pottery survive international transit at commercial breakage rates?", a: "Yes — with a four-layer packaging system engineered specifically for the fragility profile. Our blue pottery programme runs at under 0.8% breakage versus a pre-engineering industry norm of 17%." },
      { q: "How is freeze-thaw safety verified on ceramic planters?", a: "ASTM C1272 freeze-thaw testing at SGS Delhi. Body composition engineered to <5% water absorption. Firing schedule documented per SKU family." },
      { q: "How is food-contact glaze safety verified?", a: "Lead-free and cadmium-free glaze specification per food-contact SKU. LFGB and FDA migration testing per SKU family at accredited lab." },
    ],
  },
  brass: {
    overview: [
      "Brass programmes sourced from Moradabad — the world's densest concentration of brass craft (approximately 12,000 registered units). Alloy composition documented per SKU (CuZn30 for sand-cast structural, CuZn37 for spun forming). Finish library documented per SKU (electroplated, patinated, lacquered, brushed, antiqued).",
      "Case studies cover 620-SKU brass programme scaling, hospitality lighting fixtures, decorative accessories, and brass component sourcing for mixed-material assemblies.",
    ],
    faqs: [
      { q: "Why does alloy composition matter on a decorative brass SKU?", a: "Brass alloy composition determines colour, workability and structural properties. Without explicit specification, Moradabad factories select the alloy most economically convenient — producing colour and finish variance across production runs." },
      { q: "How is post-shipment tarnishing prevented?", a: "Lacquer film-thickness specification per SKU (25–40 microns for indoor, 40–60 microns for high-touch). Accelerated humidity + sulphur exposure test on golden sample verifies tarnish-resistance." },
      { q: "How is alloy composition verified?", a: "Handheld XRF spectrometer at the anchor factory verifies incoming raw-material alloy composition per batch. Out-of-spec batches rejected at raw-material stage." },
    ],
  },
  garden: {
    overview: [
      "Weather-treated outdoor décor, planters, lanterns, solar-LED path lights and garden furniture sourced across 8 clusters. UV-stable powder coating (100–120 micron with zinc-phosphate pre-treatment), 240-hour ASTM B117 salt-spray survival, freeze-thaw stable ceramic (ASTM C1272), monocrystalline Class A solar cells, LiFePO4 batteries.",
      "Case study documents an 8-year US garden-products programme that reduced 90-day return rate from 4.2% to 0.6% through material-science engineering discipline at CAD stage.",
    ],
    faqs: [
      { q: "Can Indian factories deliver outdoor-durability at US retail scale?", a: "Yes — with material-science specification enforced at CAD stage, testing at pre-production, and factory network filtered for outdoor-durability capability." },
      { q: "Why is solar cell specification such a big deal on a garden lantern?", a: "Polycrystalline Class B solar cells degrade to <30% original luminance within 14 months of field service. Monocrystalline Class A holds >70% luminance at three years." },
      { q: "What is ASTM C1272 freeze-thaw testing?", a: "American Society for Testing and Materials specification for freeze-thaw durability. Water absorption <5% is the pass threshold. Applied to ceramic planters shipped to US/Canadian cold-climate markets." },
    ],
  },
  "hotel-projects": {
    overview: [
      "5-star hospitality FF&E projects sourced for Marriott, Hilton, Jumeirah, Rotana and other international operators. Custom brass and hand-blown glass fixtures. Phased container delivery aligned to construction milestones. G-Mark, SASO, IEC 60598-1 compliance for GCC destinations.",
      "Case study documents a 340-key GCC 5-star property: 2,400 fixtures, 78 custom SKUs, 18-month project, zero site-installation slippage, zero compliance failures.",
    ],
    faqs: [
      { q: "How does phased container delivery aligned to construction milestones work?", a: "Container 1 delivers mechanical rough-in components. Container 2 delivers guestroom fixtures. Container 3 delivers public-area fixtures. Container 4 delivers outdoor and terrace." },
      { q: "How is on-site installation supervision structured?", a: "Two senior technical managers rotate on-site during each installation window. Coordination with site electrical contractor and MEP consultant." },
      { q: "How is G-Mark and SASO compliance managed for GCC hospitality?", a: "Compliance testing per SKU family at Intertek Delhi. Type-approval E-file registration per SKU family. 100% first-submission pass rate on our hospitality programme." },
    ],
  },
  "private-label": {
    overview: [
      "Private-label manufacturing programmes for retail chains, DTC brands and lifestyle retailers. Brand-firewall discipline enforced at factory-floor level. Per-brand IP protection with brand-specific NDAs. Per-brand packaging engineering with brand-book adherence.",
      "Case studies cover 84-SKU home-fragrance private label for US retailer, 180-SKU DTC metal wall art for UK brand, and 620-SKU brass programme for European retailer.",
    ],
    faqs: [
      { q: "How is per-brand IP protection enforced at factory-floor level?", a: "Every factory signs a brand-specific NDA per downstream brand. Non-copying, non-resale and post-sample destruction obligations." },
      { q: "How is per-brand packaging engineering done?", a: "Delhi NCR consolidation partner runs per-brand packaging lines. Retail-box printing per brand. Brand-book adherence verified per PO." },
      { q: "How is per-brand quality-tier discipline maintained?", a: "Each brand's quality tier documented per SKU family. Factory-floor SKU allocation matches quality tier to factory-tier." },
    ],
  },
  "oem-development": {
    overview: [
      "White-label OEM programmes for multi-brand retail groups operating 30+ downstream brands. Brand-firewall matrix per factory documenting which brands operate where, which SKUs belong to which brands, and which packaging designs belong to which brands. Consolidated Delhi NCR container consolidation.",
      "Case study documents an 8-year OEM programme with 800+ SKUs across 30+ downstream brands, zero cross-brand design leak, zero compliance holds at destination.",
    ],
    faqs: [
      { q: "What is a brand-firewall matrix?", a: "Every factory in the network holds a brand-firewall matrix documenting which brands operate in the factory, which SKU families belong to which brands, and which packaging designs belong to which brands. Enforcement audited quarterly." },
      { q: "How is OEM sourcing at 30+-brand scale operationally managed?", a: "Consolidated India-side account management with per-brand product-development leads. Per-brand IP protection via brand-specific NDAs. Per-brand packaging engineering at Delhi NCR consolidation." },
      { q: "How is cross-brand design leak prevented?", a: "Brand-firewall matrix enforcement audited quarterly. Cross-brand design elements audited and rejected at CAD stage. Zero cross-brand design leak across 8 years and 30+ downstream brands." },
    ],
  },
};

// Related services per category — deep links to service pages that back the
// topical cluster.
const CATEGORY_RELATED_SERVICES = {
  lighting: [
    { slug: "product-testing-compliance-india", name: "Product Testing & Compliance" },
    { slug: "hotel-project-procurement-india", name: "Hospitality Lighting Sourcing" },
    { slug: "quality-inspection-india", name: "Quality Inspection India" },
  ],
  furniture: [
    { slug: "furniture-sourcing-india", name: "Furniture Sourcing India" },
    { slug: "supplier-verification-india", name: "Sustainable Sourcing India" },
    { slug: "packaging-development-india", name: "Packaging Development India" },
  ],
  "home-decor": [
    { slug: "buying-agent-india", name: "Buying Agent India" },
    { slug: "supply-chain-management-india", name: "Supply Chain Management India" },
    { slug: "quality-inspection-india", name: "Quality Inspection India" },
  ],
  glass: [
    { slug: "home-decor-sourcing-india", name: "Glass Sourcing Firozabad" },
    { slug: "packaging-development-india", name: "Packaging Development India" },
    { slug: "quality-inspection-india", name: "Quality Inspection India" },
  ],
  ceramics: [
    { slug: "packaging-development-india", name: "Packaging Development India" },
    { slug: "product-testing-compliance-india", name: "Product Testing & Compliance" },
    { slug: "quality-inspection-india", name: "Quality Inspection India" },
  ],
  brass: [
    { slug: "home-decor-sourcing-india", name: "Brass Sourcing Moradabad" },
    { slug: "product-development", name: "Product Development" },
    { slug: "quality-inspection-india", name: "Quality Inspection India" },
  ],
  garden: [
    { slug: "garden-product-sourcing-india", name: "Garden & Outdoor Sourcing" },
    { slug: "product-testing-compliance-india", name: "Product Testing & Compliance" },
    { slug: "quality-inspection-india", name: "Quality Inspection India" },
  ],
  "hotel-projects": [
    { slug: "hotel-project-procurement-india", name: "Hospitality Lighting Sourcing" },
    { slug: "product-testing-compliance-india", name: "Product Testing & Compliance" },
    { slug: "supply-chain-management-india", name: "Supply Chain Management India" },
  ],
  "private-label": [
    { slug: "private-label-manufacturing-india", name: "Private Label Manufacturing India" },
    { slug: "packaging-development-india", name: "Packaging Development India" },
    { slug: "product-development", name: "Product Development" },
  ],
  "oem-development": [
    { slug: "oem-manufacturing-india", name: "OEM Product Development" },
    { slug: "private-label-manufacturing-india", name: "Private Label Manufacturing India" },
    { slug: "supply-chain-management-india", name: "Supply Chain Management India" },
  ],
};

export const CaseStudyCategory = ({ categoryIdOverride }) => {
  const params = useParams();
  const categoryId = categoryIdOverride || params.categoryId || params.slug;
  const cat = getCaseStudyCategory(categoryId);
  const studies = getCaseStudiesByCategory(categoryId);
  const ctx = CATEGORY_CONTEXT[categoryId];
  const services = CATEGORY_RELATED_SERVICES[categoryId] || [];

  useSeo({
    title: cat
      ? `${cat.name} Case Studies · India Sourcing · Asia Sourcing India`
      : "Case Studies · Asia Sourcing India",
    description: cat
      ? `${cat.tagline} ${studies.length} in-depth case studies covering ${cat.name.toLowerCase()} sourcing programmes from India.`
      : "Case studies across sourcing categories from India.",
    path: cat ? `/case-studies/${cat.id}` : "/case-studies",
    jsonLd: cat
      ? {
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "CollectionPage",
              "@id": `${SITE_URL}/case-studies/${cat.id}#collection`,
              name: `${cat.name} Case Studies — Asia Sourcing India`,
              description: cat.tagline,
              url: `${SITE_URL}/case-studies/${cat.id}`,
              isPartOf: { "@id": `${SITE_URL}/case-studies#list` },
              mainEntity: {
                "@type": "ItemList",
                numberOfItems: studies.length,
                itemListElement: studies.map((c, i) => ({
                  "@type": "ListItem",
                  position: i + 1,
                  name: c.title,
                  url: `${SITE_URL}/case-studies/${c.slug}`,
                })),
              },
            },
            ctx && ctx.faqs && ctx.faqs.length > 0
              ? {
                  "@type": "FAQPage",
                  "@id": `${SITE_URL}/case-studies/${cat.id}#faq`,
                  mainEntity: ctx.faqs.map((f) => ({
                    "@type": "Question",
                    name: f.q,
                    acceptedAnswer: { "@type": "Answer", text: f.a },
                  })),
                }
              : null,
            breadcrumb([
              { name: "Home", item: `${SITE_URL}/` },
              { name: "Case Studies", item: `${SITE_URL}/case-studies` },
              { name: cat.name, item: `${SITE_URL}/case-studies/${cat.id}` },
            ]),
          ].filter(Boolean),
        }
      : undefined,
    jsonLdId: cat ? `case-studies-category-${cat.id}-jsonld` : undefined,
  });

  if (!cat) return <Navigate to="/case-studies" replace />;

  return (
    <div data-testid={`case-study-category-${cat.id}`}>
      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="wrap" style={{ paddingTop: "calc(74px + 1.5rem)", paddingBottom: 0 }}>
        <ol style={{ display: "flex", flexWrap: "wrap", gap: ".6rem", listStyle: "none", padding: 0, margin: 0, fontSize: ".82rem", color: "var(--ink-soft)" }} data-testid="cs-cat-breadcrumb">
          <li><Link to="/">Home</Link></li>
          <li>›</li>
          <li><Link to="/case-studies">Case Studies</Link></li>
          <li>›</li>
          <li aria-current="page" style={{ color: "var(--ink)" }}>{cat.name}</li>
        </ol>
      </nav>

      {/* Header */}
      <header className="wrap" style={{ paddingBlock: "2rem 3rem" }}>
        <Reveal>
          <p className="eyebrow" data-testid="cs-cat-eyebrow">Case Studies · {cat.name}</p>
        </Reveal>
        <WordReveal
          as="h1"
          text={`${cat.name} case studies from India.`}
          className="display"
          stagger={60}
          testId="cs-cat-h1"
        />
        <Reveal as="p" delay={200} style={{ maxWidth: "72ch", color: "var(--ink-soft)", fontSize: "1.12rem", marginTop: "1.4rem", lineHeight: 1.7 }}>
          {cat.tagline}
        </Reveal>
      </header>

      {/* Category overview */}
      {ctx && ctx.overview && (
        <section className="wrap" style={{ paddingBlock: "var(--section)", borderTop: "1px solid var(--line)" }} data-testid="cs-cat-overview">
          <Reveal as="p" className="eyebrow">§ Category overview</Reveal>
          <div style={{ maxWidth: "72ch", marginTop: "1.5rem" }}>
            {ctx.overview.map((p, i) => (
              <p key={i} style={{ color: "var(--ink-soft)", fontSize: "1.08rem", lineHeight: 1.75, marginBottom: "1.4rem" }}>
                {p}
              </p>
            ))}
          </div>
        </section>
      )}

      {/* Studies grid */}
      <section className="wrap" style={{ paddingBlock: "var(--section)", borderTop: "1px solid var(--line)" }} data-testid="cs-cat-studies">
        <Reveal as="p" className="eyebrow">§ {studies.length} case {studies.length === 1 ? "study" : "studies"} in this category</Reveal>
        {studies.length === 0 ? (
          <p style={{ marginTop: "2rem", color: "var(--ink-soft)" }}>No case studies published in this category yet — check back soon.</p>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "1.4rem", marginTop: "2rem" }}>
            {studies.map((c) => (
              <Reveal key={c.slug}>
                <Link
                  to={`/case-studies/${c.slug}`}
                  style={{ display: "block", padding: "1.6rem 1.4rem", border: "1px solid var(--line)", textDecoration: "none", color: "inherit", height: "100%" }}
                  data-testid={`cs-cat-tile-${c.slug}`}
                >
                  <p className="mono" style={{ color: "var(--brass-deep)", fontSize: ".64rem", letterSpacing: ".14em", textTransform: "uppercase", margin: 0 }}>
                    {c.sector} · {c.region}
                  </p>
                  <p style={{ fontFamily: "var(--display)", fontSize: "1.15rem", margin: ".55rem 0 .6rem", letterSpacing: "-.005em", lineHeight: 1.3 }}>
                    {c.title}
                  </p>
                  <p style={{ color: "var(--ink-soft)", fontSize: ".92rem", margin: 0, lineHeight: 1.55 }}>
                    {c.duration} · {c.readTime}
                  </p>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
      </section>

      {/* Related services */}
      {services.length > 0 && (
        <section className="wrap" style={{ paddingBlock: "var(--section)", borderTop: "1px solid var(--line)" }} data-testid="cs-cat-services">
          <Reveal as="p" className="eyebrow">§ Related services</Reveal>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "1rem", marginTop: "1.5rem" }}>
            {services.map((s) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                style={{ padding: "1.2rem 1.2rem", border: "1px solid var(--line)", textDecoration: "none", color: "inherit", display: "block" }}
                data-testid={`cs-cat-service-${s.slug}`}
              >
                <p style={{ fontFamily: "var(--display)", fontSize: "1.02rem", margin: 0 }}>{s.name}</p>
                <p className="mono" style={{ color: "var(--brass-deep)", fontSize: ".64rem", letterSpacing: ".14em", textTransform: "uppercase", marginTop: ".6rem" }}>Read the service brief →</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Category FAQs */}
      {ctx && ctx.faqs && ctx.faqs.length > 0 && (
        <section className="wrap" style={{ paddingBlock: "var(--section)", borderTop: "1px solid var(--line)" }} data-testid="cs-cat-faq">
          <Reveal as="p" className="eyebrow">§ Frequently asked — {cat.name.toLowerCase()} sourcing</Reveal>
          <div style={{ maxWidth: "72ch", marginTop: "1.5rem" }}>
            {ctx.faqs.map((f, i) => (
              <details key={i} style={{ padding: "1.2rem 1.2rem", border: "1px solid var(--line)", marginBottom: ".8rem" }}>
                <summary style={{ cursor: "pointer", fontWeight: 500, fontSize: "1rem" }}>{f.q}</summary>
                <p style={{ marginTop: "1rem", color: "var(--ink-soft)", lineHeight: 1.7 }}>{f.a}</p>
              </details>
            ))}
          </div>
        </section>
      )}

      {/* Cross-link to other categories */}
      <section className="wrap" style={{ paddingBlock: "var(--section)", borderTop: "1px solid var(--line)" }} data-testid="cs-cat-siblings">
        <Reveal as="p" className="eyebrow">§ Other case-study categories</Reveal>
        <div style={{ display: "flex", flexWrap: "wrap", gap: ".7rem", marginTop: "1.5rem" }}>
          {CASE_STUDY_CATEGORIES.filter((c) => c.id !== cat.id).map((c) => (
            <Link
              key={c.id}
              to={`/case-studies/${c.id}`}
              className="chip"
              style={{ textDecoration: "none" }}
              data-testid={`cs-cat-sibling-${c.id}`}
            >
              {c.name}
            </Link>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="wrap" style={{ paddingBlock: "var(--section)", borderTop: "1px solid var(--line)" }}>
        <Reveal as="p" className="eyebrow">Ready to build your own {cat.name.toLowerCase()} programme?</Reveal>
        <Reveal as="h2" className="display">Send a sourcing brief.</Reveal>
        <Reveal as="div" style={{ display: "flex", flexWrap: "wrap", gap: "1rem", marginTop: "2rem" }}>
          <Link to="/contact" className="btn btn-fill btn-lg btn-glow" data-testid="cs-cat-cta">
            Start an enquiry
          </Link>
          <Link to="/case-studies" className="btn btn-lg">All case studies</Link>
        </Reveal>
      </section>
    </div>
  );
};

export default CaseStudyCategory;
